import React, {createContext, useContext} from 'react'

// Types matching the C# models
export interface InterpreterLanguage {
  name: string
  serviceTypes: string[]
}

export interface InterpreterPresence {
  id: string // User.Id
  userId: string // AWSUserId
  name: string
  email: string
  phone: string
  languages: InterpreterLanguage[]
  communicationTypes: string[]
  status: 'Available' | 'On Call' | 'Offline'
  hasDeviceToken: boolean
}

export interface SimplifiedPresencePayload {
  available: string[] // Array of User.Id
  onCall: string[] // Array of User.Id
}

export interface CallInfo {
  contactId: string
  callId: number
  requestedTime?: string | null
  requester: string
  customer: string
  languageFrom: string
  languageTo: string
  communicationType: string
  interpreter?: string | null
  agentId: string | null
  agentAWSUserId: string | null
  interpreterName: string | null
  InterpreterEmail: string | null
  isAiInterpretation?: boolean
  channel: string
  initiationMethod: string
  state: string
  queueTimestamp?: string | null
  connectedToAgentTimestamp?: string | null
  stateStartTimestamp?: string | null
  lastUpdated: string
}

export interface PresenceContextType {
  // State
  interpreters: InterpreterPresence[]
  callQueues: CallInfo[]
  liveCalls: CallInfo[]
  presenceSnapshot: SimplifiedPresencePayload
  isConnected: boolean
  isLoading: boolean
  error: string | null
  allInterpretersLoaded: boolean
  // Real-time agent status from SignalR broadcast
  realTimeAgentStatus: {status: string} | null

  // Computed counts
  availableCount: number
  onCallCount: number
  offlineCount: number
  totalInterpretersCount: number

  // Helper methods
  getInterpreterById: (id: string) => InterpreterPresence | undefined
  getAvailableInterpreters: () => InterpreterPresence[]
  getOnCallInterpreters: () => InterpreterPresence[]
  getOfflineInterpreters: () => InterpreterPresence[]
  refreshInterpreters: () => Promise<void>
  refreshSnapshot: () => Promise<void>
  refreshCallQueues: () => Promise<void>
  refreshLiveCalls: () => Promise<void>
}

export const initialPresenceState: PresenceContextType = {
  interpreters: [],
  callQueues: [],
  liveCalls: [],
  presenceSnapshot: {available: [], onCall: []},
  isConnected: false,
  isLoading: false,
  error: null,
  allInterpretersLoaded: false,
  realTimeAgentStatus: null,
  availableCount: 0,
  onCallCount: 0,
  offlineCount: 0,
  totalInterpretersCount: 0,
  getInterpreterById: () => undefined,
  getAvailableInterpreters: () => [],
  getOnCallInterpreters: () => [],
  getOfflineInterpreters: () => [],
  refreshInterpreters: async () => {},
  refreshSnapshot: async () => {},
  refreshCallQueues: async () => {},
  refreshLiveCalls: async () => {},
}

export const PresenceContext = createContext<PresenceContextType>(initialPresenceState)

export const usePresence = () => {
  return useContext(PresenceContext)
}
